import { createContext, useContext, useEffect, useState } from "react";
import { useSettingsQuery } from "@/api/queries/admin/SettingsQueries";

type Theme = "dark" | "light" | "system";

type ThemeProviderProps = {
    children: React.ReactNode;
    defaultTheme?: Theme;
    storageKey?: string;
};

type ThemeProviderState = {
    theme: Theme;
    setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
    theme: "system",
    setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
    children,
    defaultTheme = "system",
    storageKey = "vite-ui-theme",
    ...props
}: ThemeProviderProps) {
    const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem(storageKey) as Theme) || defaultTheme);

    const { data: settingsData } = useSettingsQuery(true);

    useEffect(() => {
        const root = window.document.documentElement;

        root.classList.remove("light", "dark");

        if (theme === "system") {
            const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

            root.classList.add(systemTheme);
            return;
        }

        root.classList.add(theme);
    }, [theme]);

    // Keep system theme in sync with OS changes
    useEffect(() => {
        if (theme !== "system") return;
        const mql = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)");
        if (!mql) return;
        const handler = () => {
            const root = document.documentElement;
            root.classList.remove("light", "dark");
            root.classList.add(mql.matches ? "dark" : "light");
        };
        try {
            if (mql.addEventListener) {
                mql.addEventListener("change", handler);
            } else if (mql.addListener) {
                mql.addListener(handler as any);
            }
        } catch {
            // Ignore errors
        }
        handler();
        return () => {
            try {
                if (mql.removeEventListener) {
                    mql.removeEventListener("change", handler);
                } else if (mql.removeListener) {
                    mql.removeListener(handler as any);
                }
            } catch {
                // Ignore errors
            }
        };
    }, [theme]);

    // Apply branding CSS variables by injecting a <style> block so cascade handles light/dark
    useEffect(() => {
        const branding = (settingsData as any)?.branding_settings;

        const toVar = (key: string) => {
            if (key === "sidebar") return "--sidebar-background";
            if (/^chart_\d$/.test(key)) return `--chart-${key.split("_")[1]}`;
            return `--${key.replace(/_/g, "-")}`;
        };

        const generateCSSVariables = (isDark = false) => {
            const lines: string[] = [];
            
            // Add font variables (only in light mode to avoid duplication)
            if (!isDark) {
                // Default fonts from tailwind.config.ts
                const defaultFontSans =
                    "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif";
                const defaultFontSerif = "Merriweather, ui-serif, Georgia, Cambria, Times New Roman, Times, serif";
                const defaultFontMono =
                    "JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace";

                // Use custom fonts if available, otherwise use defaults
                const fontSans =
                    branding?.font_sans && typeof branding.font_sans === "string" && branding.font_sans.trim()
                        ? branding.font_sans
                        : defaultFontSans;
                const fontSerif =
                    branding?.font_serif && typeof branding.font_serif === "string" && branding.font_serif.trim()
                        ? branding.font_serif
                        : defaultFontSerif;
                const fontMono =
                    branding?.font_mono && typeof branding.font_mono === "string" && branding.font_mono.trim()
                        ? branding.font_mono
                        : defaultFontMono;

                lines.push(`  --font-sans: ${fontSans};`);
                lines.push(`  --font-serif: ${fontSerif};`);
                lines.push(`  --font-mono: ${fontMono};`);
            }

            // Return early if branding is null or undefined
            if (!branding) return lines;

            Object.entries(branding).forEach(([key, value]) => {
                // Skip if value is null, undefined, not a string, or empty string
                // Empty strings should not override default CSS variables
                if (typeof value !== "string" || value.trim() === "") return;

                const isDarkKey = key.startsWith("dark_");

                if (isDark) {
                    // Dark mode: only process dark_ prefixed keys
                    if (!isDarkKey) return;
                    const cleanKey = key.replace(/^dark_/, "");
                    const cssVar = toVar(cleanKey);
                    lines.push(`  ${cssVar}: ${value};`);
                } else {
                    // Light mode: only process non-dark keys
                    if (isDarkKey) return;
                    const cssVar = toVar(key);
                    lines.push(`  ${cssVar}: ${value};`);
                }
            });

            // Handle chart-X fallbacks (hyphenated versions)
            for (let i = 1; i <= 5; i++) {
                const chartKey = `${isDark ? "dark_" : ""}chart-${i}`;
                const chartValue = (branding as any)[chartKey];
                if (typeof chartValue === "string" && chartValue.trim()) {
                    lines.push(`  --chart-${i}: ${chartValue};`);
                }
            }

            return lines;
        };

        const lightVars = generateCSSVariables(false);
        const darkVars = generateCSSVariables(true);

        // Always include at least the root section with fonts, even if no other branding
        const lightCss = lightVars.length > 0 ? `:root {\n${lightVars.join("\n")}\n}` : "";
        const darkCss = darkVars.length > 0 ? `.dark {\n${darkVars.join("\n")}\n}` : "";
        
        const css = [lightCss, darkCss].filter(Boolean).join("\n\n");

        let styleEl = document.getElementById("branding-variables") as HTMLStyleElement | null;
        if (!styleEl) {
            styleEl = document.createElement("style");
            styleEl.id = "branding-variables";
            styleEl.type = "text/css";
            document.head.appendChild(styleEl);
        }
        
        styleEl.textContent = css;

        // meta theme-color respecting current mode
        const prefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
        const isDarkNow = theme === "dark" || (theme === "system" && prefersDark);
        const metaColor = isDarkNow
            ? branding?.dark_meta_theme_color || branding?.meta_theme_color
            : branding?.meta_theme_color;
        if (metaColor) {
            let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
            if (!meta) {
                meta = document.createElement("meta");
                meta.name = "theme-color";
                document.head.appendChild(meta);
            }
            meta.content = metaColor;
        }

        // title & favicon with defaults
        if (branding?.app_title) document.title = branding.app_title;

        // Always set favicon, use custom if available, otherwise default
        const faviconUrl = branding?.favicon_url || "/pwa-192x192.png";
        const ensure = (rel: string) => {
            let l = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
            if (!l) {
                l = document.createElement("link");
                l.rel = rel;
                document.head.appendChild(l);
            }
            return l;
        };
        ensure("icon").href = faviconUrl;
        ensure("apple-touch-icon").href = faviconUrl;
        ensure("mask-icon").href = faviconUrl;

        // Expose branding strings globally for simple access in UI components
        (window as any).__BRANDING__ = {
            motd: branding?.motd_enabled === false ? undefined : branding?.motd,
            pro_tip: branding?.pro_tip_enabled === false ? undefined : branding?.pro_tip,
        };

        // Fonts are now handled via CSS variables in generateCSSVariables

        // Inject custom CSS if provided
        let customStyleEl = document.getElementById("branding-custom-css") as HTMLStyleElement | null;
        if (branding?.custom_css && typeof branding.custom_css === "string" && branding.custom_css.trim()) {
            if (!customStyleEl) {
                customStyleEl = document.createElement("style");
                customStyleEl.id = "branding-custom-css";
                customStyleEl.type = "text/css";
                document.head.appendChild(customStyleEl);
            }
            customStyleEl.textContent = branding.custom_css;
        } else if (customStyleEl) {
            // Remove custom CSS element if no custom CSS is set
            customStyleEl.remove();
        }
    }, [settingsData, theme]);

    const value = {
        theme,
        setTheme: (theme: Theme) => {
            localStorage.setItem(storageKey, theme);
            setTheme(theme);
        },
    };

    return (
        <ThemeProviderContext.Provider {...props} value={value}>
            {children}
        </ThemeProviderContext.Provider>
    );
}

export const UseTheme = () => {
    const context = useContext(ThemeProviderContext);

    if (context === undefined) throw new Error("useTheme must be used within a ThemeProvider");

    return context;
};
